/**
 * useApiQuery — GET 요청 공통 훅 (React Query useQuery 래퍼)
 *
 * 기본 동작:
 * - 윈도우 포커스 시 자동 재요청
 * - 실패 시 재시도 없음
 * - 이전 데이터 유지 (placeholderData)
 *
 * @example // 기본
 * const { data, isLoading } = useApiQuery<Member[]>({
 *   queryKey: ["members"],
 *   url: "/api/members",
 * });
 *
 * @example // 파라미터 + 타입 변환(select)
 * const { data } = useApiQuery<MemberListResponse, Member[]>({
 *   queryKey: ["members", page],
 *   url: "/api/members",
 *   params: { page, size: 20 },
 *   options: {
 *     select: (res) => res.list,
 *     enabled: !!userId,
 *   },
 * });
 */
import {
    useQuery,
    type QueryKey,
    type UseQueryOptions,
    type UseQueryResult,
} from "@tanstack/react-query";

import { defaultExtractData, getConfig } from "./configure";

export interface UseApiQueryProps<T, S = T> {
    /** React Query 캐시 키 */
    queryKey: QueryKey;
    /** GET 요청 URL */
    url: string;
    /** 쿼리 파라미터 (?key=value) */
    params?: Record<string, unknown>;
    /** useQuery 추가 옵션 (queryKey/queryFn 제외) */
    options?: Omit<UseQueryOptions<T, Error, S>, "queryKey" | "queryFn">;
}

export function useApiQuery<T, S = T>({
    queryKey,
    url,
    params,
    options,
}: UseApiQueryProps<T, S>): UseQueryResult<S, Error> {
    const { axios, extractData = defaultExtractData } = getConfig();

    return useQuery<T, Error, S>({
        queryKey,
        queryFn: async () => {
            const res = await axios.get(url, { params });
            return extractData<T>(res);
        },
        refetchOnWindowFocus: true,
        retry: false,
        // 새 데이터 로딩 중에도 이전 데이터를 유지해 레이아웃 깜빡임을 방지
        placeholderData: (prev) => prev,
        ...options,
    });
}
